import { formatDate } from '@doss/common-all'
import { Box } from '@mui/material'
import { truncate } from 'lodash'
import { useMemo, useRef, useState } from 'react'
import { FiPenTool } from 'react-icons/fi'
import { RxCross2 } from 'react-icons/rx'
import { tss } from 'tss-react'
import { gray, uiColors } from '~/common/styles/colors'
import { useGlobalContext } from '../../../../common/hooks/useGlobalContext'
import { useTypedTranslation } from '../../../../common/hooks/useTypedTranslation'
import { FileCellDocument, FileCellSignature } from '../../../records/record-cell-details/types'
import { Chip } from '../../atoms/chip/Chip'
import { ChipProps } from '../../atoms/chip/types'
import { SignatureChipPopover } from './SignatureChipPopover'

interface SignatureChipProps extends Omit<ChipProps, 'variant' | 'color'> {
  document: FileCellDocument
  signature: FileCellSignature
}

/**
 * Renders a chip with a signature timestamp and a popover
 */
export function SignatureChip({ document, signature, ...props }: SignatureChipProps) {
  const { t } = useTypedTranslation()
  const [isHovering, setIsHovering] = useState(false)
  const [isHoveringOnDeleteIcon, setIsHoveringOnDeleteIcon] = useState(false)
  const [openChipDialog, setOpenChipDialog] = useState(false)
  const signatureAnchorEl = useRef<HTMLDivElement>(null)

  const { locale, user } = useGlobalContext()
  const timestamp = formatDate(signature.createdAt, {
    localeCode: locale,
    timeZone: user?.organizationTimeZone,
  })

  const { classes } = useStyles({ isHovering, isHoveringOnDeleteIcon })

  const signatureLabel = useMemo(() => {
    const label = t('signature_chip.signed_on', { timestamp })

    return truncate(label, { length: 50, omission: '...' })
  }, [t, timestamp])

  return (
    <Box ref={signatureAnchorEl}>
      <Chip
        {...props}
        color="gray"
        // Only show the delete icon if an onDelete method is defined
        deleteIcon={
          props.onDelete ? (
            <RxCross2
              className="min-h-4 min-w-4"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                props.onDelete?.(event)
              }}
              onPointerOut={() => setIsHoveringOnDeleteIcon(false)}
              onPointerOver={() => setIsHoveringOnDeleteIcon(true)}
              size="16px"
            />
          ) : undefined
        }
        icon={<FiPenTool className={classes.icon} />}
        isRounded={false}
        label={<div className={classes.fileName}>{signatureLabel}</div>}
        onClick={() => setOpenChipDialog(true)}
        onPointerOut={() => setIsHovering(false)}
        onPointerOver={() => setIsHovering(true)}
        variant="solid"
      />
      <SignatureChipPopover
        anchorEl={signatureAnchorEl.current}
        document={document}
        onClose={() => setOpenChipDialog(false)}
        open={openChipDialog}
        signature={signature}
      />
    </Box>
  )
}

const useStyles = tss
  .withParams<{ isHovering: boolean; isHoveringOnDeleteIcon: boolean }>()
  .create(({ isHovering, isHoveringOnDeleteIcon }) => {
    return {
      icon: {
        fontSize: '12px',
        color: isHovering && !isHoveringOnDeleteIcon ? gray[9] : uiColors.content.primary,
        minWidth: '12px',
        minHeight: '12px',
      },
      fileName: {
        textDecoration: isHovering && !isHoveringOnDeleteIcon ? 'underline' : 'none',
        // Still allow user to click on the link to route to the document even if the chip is
        // disabled
        pointerEvents: 'auto',
        '&:hover': {
          cursor: 'pointer',
        },
      },
    }
  })
